<template>
  <BaseSelectDropdown
    ref="dropdown"
    :active="dropdownActive"
    :label="label"
    :label-position="labelPosition"
    :disabled="disabled"
    :dropdown-position="dropdownPosition"
    :small="small"
    :large="large"
    :allow-width-overflow="allowWidthOverflow"
    :is-in-modal="isInModal"
    :popper-class="popperClass"
    :popper-options="popperOptions"
    :lazy-render="lazyRender"
    :selected-item="selectedItem"
    :is-loading="isLoading"
    :large-with-icon="largeWithIcon"
    :placeholder-only-name="placeholderOnlyName"
    @close="$emit('close')"
    @open="$emit('open')"
    @trigger="handleDropdownTrigger"
    @handle-click-navigation="handleClickNavigation"
  >
    <template slot="label">
      <slot name="label" />
    </template>
    <template slot="header">
      <div
        v-show="showPlaceholder"
        class="select-control__placeholder"
      >
        {{ placeholder }}
      </div>
      <MSelectControlActive
        v-show="showSelected"
        :disabled="disabled"
        :payload="selected"
        :text-key="textAttr"
        :icon-key="iconKey"
        :id-key="idAttr"
        :show-id="showId"
        :label-key="labelKey"
        :description-key="descriptionKey"
        :large-with-icon="largeWithIcon"
        :placeholder-only-name="placeholderOnlyName"
        @clickBottomBtn="handleClickBottomBtn"
      />
    </template>
    <template slot="dropdown">
      <template v-if="isLoading">
        <MSelectControlSkeletonLoader />
      </template>
      <MSearchInline
        v-if="showSearch"
        ref="searchInput"
        v-model="searchText"
        :emit-input-on-change="false"
        :is-debounced="false"
        :placeholder="searchPlaceholder"
        class="m-select-control__search"
        @handle-click-navigation="handleClickNavigation"
      />
      <MSelectControlTreeLevel
        :is-visible="dropdownActive"
        :root-el-class="`m-select-control__choices-list-${_uid}`"
        :choices="filteredChoices"
        :id-attr="idAttr"
        :text-attr="textAttr"
        :icon-key="iconKey"
        :show-icon="true"
        :show-id="showId"
        :large="large"
        :selected="selected"
        :parent-is-option="parentIsOption"
        :indent-per-level="large ? 14 : 8"
        :format-fn="formatFn"
        :lazy-render="lazyRender"
        :selected-item="selectedItem"
        :label-key="labelKey"
        :description-key="descriptionKey"
        :large-with-icon="largeWithIcon"
        :current-focus-item-id="currentFocusItemId"
        :class="{
          [`m-select-control__choices-list-${_uid}`]: true,
          'm-select-control__choices-list_static': Boolean(staticOptionWidth)
        }"
        :style="{
          '--static-option-width': staticOptionWidth
        }"
        @activate="activateChoice"
        @handle-click-navigation="handleClickNavigation"
        @clickBottomBtn="handleClickBottomBtn"
      />

      <MSelectControlCreateNewOption
        v-if="showCreateNewOption"
        @addItem="handleAddItem"
      />

      <div
        v-if="showResetBtn"
        class="m-select-control__actions-container"
      >
        <MButton
          link
          gray
          class="m-select-control__actions-reset_button"
          @click="resetSelected"
        >
          {{ resetButtonText }}
        </MButton>
      </div>

      <div
        v-show="!isLoading && filteredChoices.length === 0"
        class="select-control__options-not-found"
      >
        {{ _getTr('no_matches_found') }}
      </div>
    </template>
  </BaseSelectDropdown>
</template>

<script>
  import Vue from 'vue';
  import bulletArrowIcon from 'assets/icons/bullet-arrow.svg';
  import MButton from 'components/Inputs/Basic/MButton';
  import MSelectControlActive from 'components/Inputs/Select/MSelectControl/MSelectControlActive';
  import BaseSelectDropdown from 'components/Inputs/Select/common/BaseSelectDropdown/BaseSelectDropdown.vue';
  import MSelectControlSkeletonLoader from 'components/Inputs/Select/MSelectControl/MSelectControlSkeletonLoader';
  import choicesFlat from 'components/Inputs/Select/MSelectControl/utils/choicesFlat';
  import filterChoicesTree from 'components/Inputs/Select/MSelectControl/utils/filterChoicesTree';
  import MSelectControlTreeLevel from 'components/Inputs/Select/MSelectControl/MSelectControlTreeLevel';
  import _ from 'lodash';
  import MSearchInline from 'components/common/MSearchInline';
  import getTr from 'translation';
  import MSelectControlCreateNewOption from 'components/Inputs/Select/MSelectControl/MSelectControlCreateNewOption';
  import filterSearch from 'utils/search/filterSearch';

  _.noConflict();

  export default {
    name: 'MSelectControl',
    components: {
      MButton,
      MSelectControlCreateNewOption,
      MSearchInline,
      MSelectControlTreeLevel,
      MSelectControlSkeletonLoader,
      BaseSelectDropdown,
      MSelectControlActive
    },
    props: {
      choices: {
        type: Array,
        default: Array
      },
      largeWithIcon: {
        type: Boolean,
        default: false
      },
      value: {
        type: [Object, String, Number, Boolean],
        default: null
      },
      idAttr: {
        type: String,
        default: 'id'
      },
      textAttr: {
        type: String,
        default: 'text'
      },
      descriptionKey: {
        type: String,
        default: null
      },
      labelKey: {
        type: String,
        default: null
      },
      searchFn: {
        type: Function,
        default: (item, searchQuery, component) => item[component.textAttr].toLowerCase().
          includes(searchQuery.toLowerCase())
      },
      required: {
        type: Boolean,
        default: false
      }, // defines that field must be filled
      disabled: {
        type: Boolean,
        default: false
      },
      placeholder: {
        type: String,
        default: ''
      },
      searchPlaceholder: {
        type: String,
        default: () => getTr('search')
      }, // placeholder that will be shown in search input
      search: {
        type: Boolean,
        default: false
      },
      label: {
        type: String,
        default: ''
      },
      labelPosition: {
        type: String,
        default: 'top',
        choices: ['left', 'top']
      },
      dropdownPosition: {
        type: String,
        default: 'auto',
        choices: ['bottom', 'top', 'auto']
      },
      iconKey: {
        type: String,
        default: 'icon'
      },
      small: {
        type: Boolean,
        default: false
      },
      large: {
        type: Boolean,
        default: false
      },
      isLoading: {
        type: Boolean,
        default: false
      },
      isInModal: {
        type: Boolean,
        default: false
      },
      popperClass: {
        type: BaseSelectDropdown.props.popperClass.type,
        default: BaseSelectDropdown.props.popperClass.default
      },
      parentIsOption: {
        type: Boolean,
        default: false
      },
      preserveParentInSearch: {
        type: Boolean,
        default: false
      },
      allowWidthOverflow: Boolean,
      dropdownBoundaryElement: {
        type: String,
        default: ''
      },
      showId: {
        type: Boolean,
        default: false
      },
      showCreateNewOption: {
        type: Boolean,
        default: false
      },
      showResetBtn: {
        type: Boolean,
        default: false
      },
      placeholderOnlyName: {
        type: Boolean,
        default: false
      },
      resetButtonText: {
        type: String,
        default: () => getTr('reset')
      },
      formatFn: {
        type: Function,
        default: undefined
      },
      lazyRender: {
        type: Boolean,
        default: false
      },
      staticOptionWidth: {
        type: String,
        default: 'auto'
      }
    },
    data () {
      return {
        selectedValue: '',
        selectedItem: null,
        message: null,
        dropdownActive: false,
        searchText: '',
        arrowIcon: bulletArrowIcon,
        inputWidth: 'auto',
        currentFocusItemIndex: null
      };
    },
    computed: {
      currentFocusItemId () {
        return this.itemsAvailableFocus[this.currentFocusItemIndex] || null;
      },
      itemsAvailableFocus () {
        return this.filterChoicesFlat.filter(item => !item.notAllowedForSelect).map(item => item.id);
      },
      searchTextFiltered () {
        return filterSearch(this.searchText);
      },
      popperOptions () {
        if (!this.dropdownBoundaryElement) {
          return;
        }
        return {
          modifiers: {
            preventOverflow: {
              priority: ['bottom-start']
            },
            flip: {
              boundariesElement: `#${this.dropdownBoundaryElement}`
            }
          }
        };
      },
      isSelected () {

        // check if select has value
        return this.selected !== '' && this.selected !== null;
      },
      showPlaceholder () {

        // determine should placeholder be shown
        return !this.dropdownActive && !this.isSelected ||
          !this.showSearch && this.dropdownActive && !this.isSelected;
      },
      showSearch () {

        // determine should search input be shown
        return this.dropdownActive && this.search;
      },
      showSelected () {

        // determine should selected text be shown
        return this.isSelected;
      },
      choicesFlat () {
        return choicesFlat(this.choices, 'children', this.parentIsOption);
      },
      selected () {
        if (_.isNil(this.value)) {
          return null;
        }
        return this.choicesFlat.filter(
          item => _.isEqual(item[this.idAttr], this.value)
        )[0];
      },
      filteredChoices () {
        return filterChoicesTree(this.choices, this.searchFn, this.searchTextFiltered, this,
                                 'children', this.parentIsOption || this.preserveParentInSearch);
      },
      filterChoicesFlat () {
        return choicesFlat(this.filteredChoices, 'children', this.parentIsOption);
      },
      firstChoice () {
        return this.choicesFlat[0] || null;
      },
      needToSetRequiredValue () {
        return this.required && this.firstChoice && this.value === null;
      }
    },
    watch: {
      itemsAvailableFocus: {
        handler () {
          this.currentFocusItemIndex = null;
        },
        deep: true
      },
      currentFocusItemIndex (newValue) {
        if (newValue !== null) {
          this.unFocusSearchInput();
        }
      },
      value: {
        handler () {

          // sync outside and inside values
          this.selectedValue = this.value;
          this.setDefaultStateNavigation();
        },
        immediate: true
      },
      showSearch (flag) {
        if (flag) {

          // Have no idea how to make it better without a little bit of dirty hacks
          const triggerWidth = this.$el.offsetWidth;

          // trigger width - (select trigger padding + dropdown trigger icon + some magic number :D)
          this.inputWidth = `${triggerWidth - 42}px`;

          // in current tick input element is not rendered (cannot focus on not shown element)
          Vue.nextTick(() => {
            if (this.$refs.searchInput) {
              this.$refs.searchInput.focus();
            }
          });
        }
      },
      needToSetRequiredValue: {
        handler () {
          if (this.needToSetRequiredValue) {
            this.activateChoice(this.firstChoice);
          }
        },
        immediate: true
      }
    },
    methods: {
      setDefaultStateNavigation () {
        this.currentFocusItemIndex = this.getIndexFocusItem(this.value);
      },
      getIndexFocusItem (id) {
        let resultIndex = null;

        for (let index = 0; index < this.itemsAvailableFocus.length; index++) {
          if (this.itemsAvailableFocus[index] === id) {
            resultIndex = index;
            break;
          }
        }

        return resultIndex;
      },
      onFocusCurrentComponent () {
        this.$refs.dropdown?.$el.focus();
      },
      unFocusSearchInput () {
        if (!this.showSearch) {
          return;
        }

        const searchInputComponent = this.$refs?.searchInput?.$refs?.input;
        if (searchInputComponent) {
          searchInputComponent.blur();
          this.onFocusCurrentComponent();
        } else {
          this.$nextTick(this.unFocusSearchInput);
        }
      },
      handleClickBottomBtn (value) {
        this.$emit('clickBottomBtn', value);
      },
      handleClickNavigation (e) {
        e.stopPropagation();

        if (['ArrowUp', 'ArrowDown', 'Enter'].includes(e.key) && this.dropdownActive === false) {
          this.handleDropdownTrigger(true);
          return;
        }

        switch (e.key) {
        case 'ArrowUp':
          if (!this.currentFocusItemIndex) {
            break;
          }

          this.currentFocusItemIndex = this.currentFocusItemIndex - 1;
          break;
        case 'ArrowDown':
          if (this.currentFocusItemIndex === null && this.itemsAvailableFocus.length > 0) {
            this.currentFocusItemIndex = 0;
            break;
          } else if (this.itemsAvailableFocus.length - 1 === this.currentFocusItemIndex) {
            break;
          }

          this.currentFocusItemIndex = this.currentFocusItemIndex + 1;
          break;
        case 'Enter':
          if (!this.currentFocusItemId && this.currentFocusItemId !== 0) {
            break;
          }

          if (['number', 'string'].includes(typeof this.currentFocusItemId)) {
            this.activateChoiceWithId(this.currentFocusItemId);
          }

          break;
        case 'Escape':
          this.handleDropdownTrigger(false);
          break;
        default:
          break;
        }
      },
      handleDropdownTrigger (state) {
        if (state) {
          this.searchText = '';

          if (['number', 'string'].includes(typeof this.currentFocusItemId)) {
            this.$nextTick(this.unFocusSearchInput);
          }
        } else {
          this.onFocusCurrentComponent();
        }

        this.setDefaultStateNavigation();
        this.dropdownActive = state;
      },
      closeDropdown () {

        // close dropdown
        if (this.$refs.dropdown) {
          this.$refs.dropdown.closeDropdown();
        }
      },
      activateChoiceWithId (itemId) {
        const item = this.choicesFlat.find(item => item.id === itemId) || null;
        this.activateChoice(item);
      },
      activateChoice (item) {

        // is optgroupm-select-control__choices-list_static
        if (!this.parentIsOption && item.children && item.children.length > 0) {
          console.warn('isOptgroup');
          return;
        }

        if (this.disabled) {
          console.warn('disabled');
          return;
        }

        if (item.disabled) {
          console.warn('itemDisabled');
          return;
        }

        // do not trigger input if same value already selected
        if (this.value === item[this.idAttr]) {
          if (!this.selectedItem) {
            const isChoicesWithChildren = this.choices.some(item => item.children);

            this.selectedItem = {...item, isChoicesWithChildren};
            return;
          }

          console.warn('isAlreadySelected');
          return;
        }

        this.selectedItem = item;
        this.selectedValue = item[this.idAttr];
        this.$emit('input', item[this.idAttr]);

        this.dropdownActive = false;
      },
      resetState () {

        // clears search text
        this.searchText = '';
      },
      getNameByValue (value) {

        // gets name by value, not working with child elements
        const data = this.choices.filter(item => item[this.idAttr] === value)[0];
        if (data) {
          return data[this.textAttr];
        }
        return null;
      },
      handleAddItem () {
        this.dropdownActive = false;
        this.$emit('addItem');
      },
      resetSelected () {
        this.selectedValue = '';
        this.resetState();
        this.$emit('input', null);
        this.dropdownActive = false;
      }
    }
  };
</script>

<style>
  .select-control__placeholder {
    font-size: var(--main-font-size);

    color: var(--gray-text-color);
    text-overflow: ellipsis;

    overflow: hidden;
  }

  .select-container_small .select-control__placeholder {
    font-size: var(--secondary-font-size);
  }

  .select-options_loading .select-options__skeleton-item {
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .select-options_loading .select-options__skeleton-item:first-child {
    padding-top: 12px;
  }

  .select-control__grouped_options {
    padding: 0;

    & > * {
      text-overflow: ellipsis;

      overflow: hidden;

      padding-left: 14px;
      padding-right: 24px;
    }
  }

  .m-error-wrapper__active .select-control {
    border-color: var(--error-color);
  }

  .select-control__options-not-found {
    font-size: 12px;

    color: var(--gray-text-color);

    margin: 4px 8px;
  }

  .m-select-control__search {
    padding: 12px 8px 8px;
  }

  .m-select-control__actions-container {
    text-align: right;

    position: sticky;
    bottom: 0;

    padding: 8px 18px 4px 8px;

    background-color: var(--background-color);
  }

  .m-select-control__actions-reset_button {
    font-size: 16px;
    font-weight: 500;
  }

  .m-select-control__choices-list_static.m-select-control-tree-level {
    width: var(--static-option-width);
  }
</style>
